<template>
    <div class="page-wrapper billing-page">
        <ExplanationTitle heading="Billing" explanation=""/>

        <!-- Subscription Info -->
        <div class="row">
            <div class="col-lg-6">
                <div class="billing-card">
                    <div v-if="subscriptionLoading" class="loading-effect">
                        <div class="spinner-border"></div>
                    </div>

                    <div v-else-if="subscriptionError" class="empty-state">
                        <p>{{ subscriptionError }}</p>
                    </div>

                    <div v-else>
                        <div class="info-row">
                            <span class="info-label">Status</span>
                            <span :class="['status-badge', 'status-' + subscription.status]">{{ subscription.status }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Started</span>
                            <span class="info-value">{{ formatDate(subscription.started_at) }}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Projects</span>
                            <span class="info-value">{{ subscription.project_count }} <span v-if="subscription.addon_quantity > 0">({{ subscription.addon_quantity }} extra)</span></span>
                        </div>
                        <div class="info-row" v-if="subscription.next_billed_at">
                            <span class="info-label">Next payment</span>
                            <span class="info-value">{{ formatDate(subscription.next_billed_at) }}<template v-if="subscription.next_payment_amount !== null"> &mdash; <strong>${{ subscription.next_payment_amount.toFixed(2) }}</strong></template></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Payment History -->
        <div class="row mt-4">
            <div class="col-lg-12">
                <div class="billing-card">
                    <h1>Payment History</h1>

                    <div class="table-wrapper">
                        <table class="table transactions-table">
                            <thead>
                            <tr>
                                <th>Transaction ID</th>
                                <th>Product</th>
                                <th>Paid</th>
                                <th>Status</th>
                                <th>Date</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="loaded && transactions.length === 0">
                                <td colspan="5" class="text-center py-5">
                                    <p class="empty-text">No transactions found</p>
                                </td>
                            </tr>
                            <tr v-if="!loaded">
                                <td colspan="5">
                                    <div class="loading-effect">
                                        <div class="spinner-border"></div>
                                    </div>
                                </td>
                            </tr>
                            <tr v-for="(transaction, id) in transactions" :key="id">
                                <td><span class="transaction-id">{{ transaction.transaction_id }}</span></td>
                                <td>{{ transaction.description || '—' }}</td>
                                <td><strong :class="{'text-danger': transaction.amount < 0}">{{ formatAmount(transaction.amount) }}</strong></td>
                                <td>
                                    <span :class="['status-badge', 'status-' + transaction.status]">{{ transaction.status }}</span>
                                </td>
                                <td>{{ transaction.date }}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Cancel Subscription -->
        <div class="row mt-4" v-if="!subscriptionLoading && !subscriptionError && subscription.status === 'active'">
            <div class="col-lg-6">
                <div class="billing-card billing-card-danger">
                    <h1>Cancel Subscription</h1>
                    <p>Delete your subscription permanently. Once cancelled, your access to FormWise will be immediately revoked and you will be logged out.</p>
                    <a href="#" @click.prevent="confirmCancel" :class="{'btn btn-danger': true, 'disabled': cancelling}">
                        <span class="spinner-border" v-if="cancelling"></span>
                        <span :class="{'opacity-0': cancelling}">Cancel Subscription</span>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import ExplanationTitle from "@/components/widgets/ExplanationTitle.vue";
import repository from "@/repository/repository";

export default {
    name: "Billing",
    components: {ExplanationTitle},
    data() {
        return {
            loaded: false,
            transactions: [],
            subscriptionLoading: true,
            subscriptionError: null,
            subscription: {
                started_at: null,
                next_billed_at: null,
                status: null,
                project_count: 0,
                addon_quantity: 0,
                next_payment_amount: null,
            },
            cancelling: false,
        }
    },
    created() {
        this.loadTransactions();
        this.loadSubscription();
    },
    methods: {
        loadTransactions() {
            repository.get("/transactions")
                .then(response => {
                    this.transactions = response.data.transactions;
                    this.loaded = true;
                })
                .catch(error => {
                    console.log(error);
                });
        },
        loadSubscription() {
            this.subscriptionLoading = true;
            repository.get("/subscription")
                .then(response => {
                    this.subscription = response.data.subscription;
                    this.subscriptionLoading = false;
                })
                .catch(error => {
                    this.subscriptionError = "Could not load subscription details.";
                    this.subscriptionLoading = false;
                    console.log(error);
                });
        },
        confirmCancel() {
            this.$confirm({
                title: "Cancel your subscription?",
                message: "This will immediately cancel your subscription and revoke access to FormWise. You will be logged out. This action cannot be undone.",
                button: {
                    yes: "Yes, cancel subscription",
                    no: "No, keep subscription"
                },
                callback: confirm => {
                    if (confirm) {
                        this.cancelSubscription();
                    }
                }
            });
        },
        cancelSubscription() {
            this.cancelling = true;
            repository.post("/subscription/cancel")
                .then(() => {
                    this.$store.commit("logoutUser");
                    window.location.href = process.env.VUE_APP_LANDING_URL;
                })
                .catch(error => {
                    this.cancelling = false;
                    console.log(error);
                    this.$confirm({
                        title: "Something went wrong",
                        message: "Could not cancel your subscription. Please try again later.",
                        button: { yes: "OK" }
                    });
                });
        },
        formatAmount(amount) {
            if (amount < 0) {
                return '-$' + Math.abs(amount).toFixed(2);
            }
            return '$' + amount.toFixed(2);
        },
        formatDate(dateString) {
            if (!dateString) return "\u2014";
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../scss/variables";

.billing-page {
    padding-bottom: 5rem;
}

// Shared card style — matches fields-wrapper pattern from EditForm
.billing-card {
    background: $white;
    margin-bottom: 2.5rem;
    border-radius: $box-border-radius;

    h1 {
        font-weight: 600;
        font-size: 1.25rem;
        color: $dark;
        margin: 0 0 1.5rem 0;
        padding-bottom: 1.5rem;
        border-bottom: 1px solid rgba($dark, 0.1);
    }

    p {
        color: $grey-text;
        font-size: 0.95rem;
        line-height: 1.6;
        margin: 0 0 1.5rem 0;
    }

    .loading-effect {
        padding: 0.5rem 0;

        .spinner-border {
            opacity: 0.25;
            border-width: 2px;
            width: 1.5rem;
            height: 1.5rem;
        }
    }

    .empty-state {
        p {
            margin: 0;
        }
    }

    // Danger variant — matches delete-form-box style
    &.billing-card-danger {
        h1 {
            border-bottom-color: rgba($danger, 0.15);
        }

        .btn {
            display: inline-flex;
            align-items: center;
        }
    }
}

// Subscription info rows
.info-row {
    display: flex;
    align-items: center;
    padding: 0.85rem 0;
    border-bottom: 1px solid $border-grey;

    &:first-child {
        padding-top: 0;
    }

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    .info-label {
        font-weight: 600;
        color: $dark;
        width: 140px;
        flex-shrink: 0;
        font-size: 0.95rem;
    }

    .info-value {
        color: $grey-text;
        font-size: 0.95rem;
    }

    @include smartphone {
        .info-label {
            width: 110px;
        }
    }
}

// Status badges — used in both subscription and transactions
.status-badge {
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.35rem 0.75rem;
    border-radius: $box-border-radius;
    text-transform: capitalize;
    display: inline-block;

    &.status-active {
        background-color: rgba(0, 128, 0, 0.1);
        color: green;
    }

    &.status-paid {
        background-color: rgba(0, 128, 0, 0.1);
        color: green;
    }

    &.status-canceled, &.status-cancelled {
        background-color: rgba($danger, 0.1);
        color: $danger;
    }

    &.status-paused {
        background-color: rgba($primary, 0.1);
        color: $primary;
    }

    &.status-free {
        background-color: rgba($primary, 0.1);
        color: $primary;
    }
}

// Payment history table
.table-wrapper {
    @include tablet {
        overflow: scroll;
        white-space: nowrap;
    }

    @include smartphone {
        overflow: scroll;
        white-space: nowrap;
    }
}

.empty-text {
    color: $grey-text;
    margin: 0;
}

.table.transactions-table {
    margin-bottom: 0;

    thead {
        tr {
            th {
                padding: 0 1rem 1rem 0;
                color: $dark;
                border-top: none;
                border-bottom: none;
                font-size: 0.95rem;
                font-weight: 600;
            }
        }
    }

    tbody {
        tr {
            td {
                padding: 1rem 1rem 1rem 0;
                vertical-align: middle;
                color: $grey-text;
                font-size: 0.95rem;

                strong {
                    color: $dark;
                    font-weight: 600;

                    &.text-danger {
                        color: $danger;
                    }
                }

                .transaction-id {
                    color: $dark;
                    max-width: 200px;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    display: block;
                    font-size: 0.9rem;
                    font-family: monospace;
                }
            }
        }
    }
}
</style>
